/**
 * Shared error helpers for openapi-fetch results. Several endpoints in the
 * generated client don't declare error responses, so the typed `error` is
 * `never`. We still need to handle non-2xx at runtime — Response carries that.
 */

import { DomainError, type ApiError } from '@leanin/shared/errors';

export type FailureResult = { ok: false; code: string; message: string };

export type ApiResult = { ok: true } | FailureResult;

export async function failureFrom(
  response: Response,
  fallback: string,
): Promise<FailureResult> {
  let code = `http_${response.status}`;
  let message = fallback;
  try {
    const body = (await response.clone().json()) as {
      code?: unknown;
      message?: unknown;
    };
    if (typeof body.code === 'string') code = body.code;
    if (typeof body.message === 'string') message = body.message;
  } catch {
    // Body wasn't JSON — keep the fallback message.
  }
  return { ok: false, code, message };
}

export function isOk(response: Response): boolean {
  return response.ok;
}

/**
 * Unwrap an openapi-fetch result for a body-returning endpoint: returns the
 * success payload, or throws a `DomainError` carrying the server's error code
 * AND the HTTP status (via `response`). Passing `response` is what keeps a
 * gateway timeout surfacing as `http_504` instead of the statusless
 * `http_error` — this helper is the single chokepoint that makes it impossible
 * to drop the status at a call site (the bug behind the bulk-invite report).
 *
 * Use at openapi-fetch mutation/query sites instead of the hand-rolled
 * `if (error !== undefined || data === undefined) throwApiError(error)`.
 */
export function unwrap<T>(result: {
  data?: T;
  error?: unknown;
  response: Response;
}): T {
  if (result.data === undefined || result.error !== undefined) {
    throwApiError(result.error, result.response);
  }
  return result.data;
}

/**
 * Companion to `unwrap` for no-content (204) endpoints that return no body on
 * success: throws (with status) on any non-2xx, returns void otherwise.
 */
export function unwrapVoid(result: {
  error?: unknown;
  response: Response;
}): void {
  if (!result.response.ok) {
    throwApiError(result.error, result.response);
  }
}

/**
 * The two "this resource isn't there" statuses. 404 is never-existed or
 * not-visible-to-you; 410 (`GONE`) is public content that has since been
 * deleted — the public resource and learning-track endpoints return it in
 * place of 404 so crawlers can drop the URL. Both must land on the branded
 * not-found surface, never the generic "Couldn't load …" retry surface.
 */
export type NotFoundStatus = 404 | 410;

/**
 * Marker error thrown by `throwApiError` when the underlying response was
 * one of `NotFoundStatus`. Detail-page routes test for this via
 * `isNotFoundApiError(err)` and re-throw `notFound()` from
 * `@tanstack/react-router` so the branded 404 page renders instead of the
 * generic "Couldn't load …" surface. `status` carries which of the two it
 * was, for callers that need to distinguish gone from missing.
 */
export class NotFoundApiError extends DomainError {
  readonly status: NotFoundStatus;
  constructor(code: string, status: NotFoundStatus) {
    super({ code } as ApiError);
    this.status = status;
    Object.setPrototypeOf(this, NotFoundApiError.prototype);
    this.name = 'NotFoundApiError';
  }
}

export function isNotFoundApiError(err: unknown): err is NotFoundApiError {
  return err instanceof NotFoundApiError;
}

/**
 * Single typed entry point for narrowing openapi-fetch `error` payloads to
 * the server's `code` string. The wire shape is `{ error: { code, message,
 * details? } }`; centralising the assertion here means an envelope-shape
 * evolution only touches this module.
 */
export function parseApiErrorCode(error: unknown): string | null {
  const inner = (error as { error?: { code?: unknown } } | undefined)?.error;
  if (inner === null || inner === undefined) return null;
  return typeof inner.code === 'string' ? inner.code : null;
}

/**
 * Companion to `parseApiErrorCode` for routes that need the validation
 * `details` envelope (per-field arrays). Returns `null` when the field is
 * absent or shape-mismatched.
 */
export function parseApiErrorDetails(
  error: unknown,
): Record<string, ReadonlyArray<string>> | null {
  const inner = (error as { error?: { details?: unknown } } | undefined)?.error;
  if (inner === null || inner === undefined) return null;
  const details = inner.details;
  if (details === null || typeof details !== 'object') return null;
  return details as Record<string, ReadonlyArray<string>>;
}

/**
 * Adapt a VALIDATION envelope's per-field `details` into the mutable
 * `Record<string, string[]>` shape form components hold in their
 * `fieldErrors` state, so a server-side rejection lands on the field that
 * caused it instead of only in the form-level banner. Returns `null` when
 * the error carries no per-field detail.
 */
export function fieldErrorsFromApiError(
  err: unknown,
): Record<string, string[]> | null {
  const details = parseApiErrorDetails(err);
  if (details === null) return null;
  const entries: Array<[string, string[]]> = Object.entries(details).map(
    ([field, messages]) => [field, [...messages]],
  );
  return entries.length > 0 ? Object.fromEntries(entries) : null;
}

function validationDetailsSummary(error: unknown): string | null {
  const details = parseApiErrorDetails(error);
  if (details === null) return null;
  const parts = Object.entries(details).map(
    ([field, messages]) => `${field}: ${messages.join(', ')}`,
  );
  return parts.length > 0 ? parts.join('; ') : null;
}

/**
 * Companion to `parseApiErrorCode` for surfaces that need the server's
 * human-readable message.
 */
export function parseApiErrorMessage(error: unknown): string | null {
  const inner = (error as { error?: { message?: unknown } } | undefined)?.error;
  if (inner === null || inner === undefined) return null;
  return typeof inner.message === 'string' ? inner.message : null;
}

/**
 * Re-throws an openapi-fetch `error` value as a `DomainError`, preserving the
 * server's `code` from the body envelope. Use at api-client mutation sites
 * where the existing action layer expects to catch a `DomainError` and run it
 * through `failureFromError()`.
 *
 * Pass `response` so a 404 or 410 becomes a typed `NotFoundApiError`;
 * detail-page routes (`/members/{id}`, `/circles/{id}`, …) check for this
 * and render the branded NotFoundPage instead of a generic retry surface.
 */
export function throwApiError(error: unknown, response?: Response): never {
  // When the body carries our `{ error: { code } }` envelope, that code wins.
  // Otherwise the response came from infrastructure (gateway 502/504, body not
  // JSON) — fall back to `http_<status>` so the failure is triageable, NOT the
  // statusless `http_error`. Only with no `response` at all (a fetch-level
  // throw before any response) do we use the bare `http_error`.
  const code =
    parseApiErrorCode(error) ??
    (response !== undefined ? `http_${response.status}` : 'http_error');
  const status = response?.status;
  if (status === 404 || status === 410) {
    throw new NotFoundApiError(code, status);
  }
  // Re-throw the server's full error object (code + message + details), not
  // just the code — `failureFromError` downstream reads `message` and the
  // VALIDATION per-field `details` from the same envelope shape.
  const inner = (error as { error?: unknown } | undefined)?.error;
  if (inner !== null && typeof inner === 'object' && 'code' in inner) {
    throw new DomainError(inner as ApiError);
  }
  throw new DomainError({ code } as ApiError);
}

/**
 * Convert a thrown error (typically a `DomainError`) into the same
 * `{ ok: false, code, message }` shape `failureFrom` produces for HTTP
 * responses. Used by server actions that call services in-process: the
 * service throws on failure, the action translates to the wire shape.
 */
export function failureFromError(err: unknown, fallback: string): FailureResult {
  // Wire envelope first — `parseApiErrorCode`/`Message` is the canonical
  // narrowing surface (it owns the `as` cast). Fall through to the
  // top-level `{ code, message }` shape that thrown `DomainError`s
  // produce in-process.
  const envelopeCode = parseApiErrorCode(err);
  if (envelopeCode !== null) {
    // VALIDATION responses carry no top-level message — the substance is in
    // the per-field `details` map. Surface it instead of the opaque fallback
    // so the user (and we, debugging from a screenshot) can see which field
    // the server rejected.
    const message =
      parseApiErrorMessage(err) ??
      (envelopeCode === 'VALIDATION' ? validationDetailsSummary(err) : null) ??
      fallback;
    return { ok: false, code: envelopeCode, message };
  }
  if (err !== null && typeof err === 'object') {
    const e = err as { code?: unknown; message?: unknown };
    const code = typeof e.code === 'string' ? e.code : 'UNKNOWN';
    const message = typeof e.message === 'string' ? e.message : fallback;
    return { ok: false, code, message };
  }
  return { ok: false, code: 'UNKNOWN', message: fallback };
}
